/**
 * Public CMS renderers (Feb 2026).
 *
 * These are the read-side counterparts of `/admin/pages`. They fetch
 * from `/api/public/cms/*`, which enforces visibility server-side — the
 * client-side rendering assumes the response is authoritative.
 *
 * Meta tags (title / description / canonical / Open Graph / Twitter /
 * JSON-LD) are injected via react-helmet-async. Structured data for
 * articles emits schema.org/Article, and pages emit schema.org/WebPage.
 * DOMPurify is layered on top of the server-side sanitizer as defence
 * in depth.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import DOMPurify from 'dompurify';
import { PublicShell } from './PublicShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** DOMPurify config aligned with the backend sanitizer's allow-list. */
const PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'a', 'img', 'iframe',
    'ul', 'ol', 'li', 'blockquote', 'hr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'table', 'thead', 'tbody', 'tr', 'td', 'th',
    'figure', 'figcaption', 'div', 'span', 'code', 'pre',
  ],
  ALLOWED_ATTR: [
    'href', 'target', 'rel', 'title',
    'src', 'alt', 'width', 'height',
    'allow', 'allowfullscreen', 'frameborder', 'referrerpolicy',
    'colspan', 'rowspan', 'class',
  ],
  ADD_TAGS: ['iframe'],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|\/|#)/i,
};

function absolute(url) {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
}

/** Shared meta-tag block used by both Page and Article renderers. */
function CmsHelmet({ doc, canonicalPath, articleSchema }) {
  const seo = doc.seo || {};
  const article = doc.article || {};
  const title       = seo.seo_title || doc.title;
  const description = seo.meta_description || article.excerpt || '';
  const canonical   = seo.canonical_url || absolute(canonicalPath);
  const ogTitle     = seo.social_title || title;
  const ogDesc      = seo.social_description || description;
  const ogImage     = seo.social_image || article.featured_image || null;
  const shouldNoIndex =
    doc.visibility !== 'public' || doc.status !== 'published' || doc.seo_index === false;

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      {description && <meta name="description" content={description} />}
      {shouldNoIndex
        ? <meta name="robots" content="noindex, nofollow" />
        : <meta name="robots" content="index, follow" />}
      <link rel="canonical" href={canonical} />
      <meta property="og:type" content={doc.content_type === 'article' ? 'article' : 'website'} />
      <meta property="og:title" content={ogTitle} />
      {ogDesc && <meta property="og:description" content={ogDesc} />}
      <meta property="og:url" content={canonical} />
      {ogImage && <meta property="og:image" content={absolute(ogImage)} />}
      <meta name="twitter:card" content={ogImage ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:title" content={ogTitle} />
      {ogDesc && <meta name="twitter:description" content={ogDesc} />}
      {ogImage && <meta name="twitter:image" content={absolute(ogImage)} />}
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}
    </Helmet>
  );
}

function articleJsonLd(doc, canonical) {
  const a = doc.article || {};
  const seo = doc.seo || {};
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: doc.title,
    description: seo.meta_description || a.excerpt || undefined,
    image: a.featured_image ? [absolute(a.featured_image)] : undefined,
    author: a.author ? { '@type': 'Person', name: a.author } : undefined,
    datePublished: a.publication_date || doc.published_at || undefined,
    dateModified: doc.updated_at || doc.published_at || undefined,
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
  };
}

function pageJsonLd(doc, canonical) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: doc.title,
    url: canonical,
    description: (doc.seo || {}).meta_description || undefined,
    dateModified: doc.updated_at || doc.published_at || undefined,
  };
}

function RenderBody({ html }) {
  const clean = DOMPurify.sanitize(html || '', PURIFY_CONFIG);
  return (
    <div
      className="cms-body prose-slate max-w-none"
      data-testid="cms-body"
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}

// ── Page renderer ────────────────────────────────────────────────────
export function PublicPage({ slugOverride, previewDoc }) {
  const params = useParams();
  const slug = slugOverride || params.slug;
  const nav = useNavigate();
  const [doc, setDoc] = useState(previewDoc || null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(!previewDoc);

  useEffect(() => {
    if (previewDoc) return;
    setLoading(true); setErr(null);
    fetch(`${API}/public/cms/by-slug?slug=${encodeURIComponent(slug)}&content_type=page`,
          { credentials: 'include' })
      .then(async r => {
        if (r.status === 404) { nav('/404-cms', { replace: true }); return null; }
        const b = await r.json().catch(() => ({}));
        if (!r.ok) { setErr(b.detail || 'Not available'); return null; }
        return b;
      })
      .then(d => d && setDoc(d))
      .finally(() => setLoading(false));
  }, [slug, previewDoc, nav]);

  if (loading) return <PublicShell canonicalPath={`/${slug}`}><Loading /></PublicShell>;
  if (err) return <PublicShell canonicalPath={`/${slug}`}><ErrorBlock text={err} /></PublicShell>;
  if (!doc) return null;

  const canonical = absolute(`/${doc.slug}`);
  return (
    <PublicShell canonicalPath={`/${doc.slug}`}>
      <CmsHelmet doc={doc} canonicalPath={`/${doc.slug}`} articleSchema={pageJsonLd(doc, canonical)} />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <h1 className="font-['Montserrat'] text-3xl sm:text-4xl font-bold text-[#0F172A] mb-4">
          {doc.title}
        </h1>
        <RenderBody html={doc.content_html} />
      </article>
    </PublicShell>
  );
}

// ── Article renderer ─────────────────────────────────────────────────
export function PublicArticle() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setErr(null);
    fetch(`${API}/public/cms/by-slug?slug=${encodeURIComponent(slug)}&content_type=article`,
          { credentials: 'include' })
      .then(async r => {
        if (r.status === 404) { nav('/404-cms', { replace: true }); return null; }
        const b = await r.json().catch(() => ({}));
        if (!r.ok) { setErr(b.detail || 'Not available'); return null; }
        return b;
      })
      .then(d => d && setDoc(d));
  }, [slug, nav]);

  if (err) return <PublicShell canonicalPath={`/blog/${slug}`}><ErrorBlock text={err} /></PublicShell>;
  if (!doc) return <PublicShell canonicalPath={`/blog/${slug}`}><Loading /></PublicShell>;

  const canonical = absolute(`/blog/${doc.slug}`);
  const a = doc.article || {};
  return (
    <PublicShell canonicalPath={`/blog/${doc.slug}`}>
      <CmsHelmet doc={doc} canonicalPath={`/blog/${doc.slug}`} articleSchema={articleJsonLd(doc, canonical)} />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        {a.featured_image && (
          <img
            src={a.featured_image}
            alt={doc.title}
            className="w-full h-auto rounded-lg mb-6"
            data-testid="cms-article-featured"
          />
        )}
        <div className="flex flex-wrap items-center gap-2 text-xs text-[#64748B] mb-3">
          {a.category && <span className="uppercase tracking-wider font-semibold text-[#1A3E6F]">{a.category}</span>}
          {a.publication_date && <span>{new Date(a.publication_date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</span>}
          {a.author && <span>· By {a.author}</span>}
        </div>
        <h1 className="font-['Montserrat'] text-3xl sm:text-4xl font-bold text-[#0F172A] mb-4">
          {doc.title}
        </h1>
        {a.excerpt && <p className="text-lg text-[#475569] mb-6">{a.excerpt}</p>}
        <RenderBody html={doc.content_html} />
        {Array.isArray(a.tags) && a.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-[#E2E8F0]">
            {a.tags.map(t => (
              <span key={t} className="text-xs bg-[#F1F5F9] text-[#475569] px-2 py-1 rounded">#{t}</span>
            ))}
          </div>
        )}
        <div className="mt-10">
          <Link to="/blog" className="text-sm text-[#1A3E6F] hover:underline">← Back to blog</Link>
        </div>
      </article>
    </PublicShell>
  );
}

// ── Blog index (public list of PUBLIC published articles) ────────────
export function PublicBlogIndex() {
  const [state, setState] = useState({ rows: [], page: 1, has_more: false, total: 0 });
  const [loading, setLoading] = useState(true);

  const load = (page = 1) => {
    setLoading(true);
    fetch(`${API}/public/cms/blog?page=${page}&page_size=10`)
      .then(r => r.json())
      .then(d => setState(d))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(1); }, []);

  return (
    <PublicShell canonicalPath="/blog">
      <Helmet prioritizeSeoTags>
        <title>Blog — satisFIND</title>
        <meta name="description" content="Latest news, articles and insights from the satisFIND team." />
        <link rel="canonical" href={absolute('/blog')} />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <h1 className="font-['Montserrat'] text-3xl sm:text-4xl font-bold text-[#0F172A] mb-2">Blog</h1>
        <p className="text-[#64748B] mb-8">News, insights and articles from satisFIND.</p>
        {loading && state.rows.length === 0 && <Loading />}
        {!loading && state.rows.length === 0 && (
          <p className="text-[#94A3B8] text-sm" data-testid="cms-blog-empty">No published articles yet. Check back soon.</p>
        )}
        <div className="space-y-6" data-testid="cms-blog-list">
          {state.rows.map(r => {
            const a = r.article || {};
            return (
              <Link
                key={r.content_id}
                to={`/blog/${r.slug}`}
                className="block bg-white border border-[#E2E8F0] rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                data-testid={`cms-blog-card-${r.slug}`}
              >
                {a.featured_image && (
                  <div className="aspect-video bg-[#F1F5F9] overflow-hidden">
                    <img src={a.featured_image} alt={r.title} loading="lazy"
                         className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="p-5">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-[#64748B] mb-2">
                    {a.category && <span className="uppercase tracking-wider font-semibold text-[#1A3E6F]">{a.category}</span>}
                    {a.publication_date && <span>{new Date(a.publication_date).toLocaleDateString()}</span>}
                    {a.author && <span>· {a.author}</span>}
                  </div>
                  <h2 className="text-xl font-bold text-[#0F172A] mb-2">{r.title}</h2>
                  {a.excerpt && <p className="text-sm text-[#475569]">{a.excerpt}</p>}
                </div>
              </Link>
            );
          })}
        </div>
        {(state.page > 1 || state.has_more) && (
          <div className="flex items-center justify-between mt-8 text-sm">
            <button
              disabled={state.page <= 1}
              onClick={() => load(state.page - 1)}
              className="text-[#1A3E6F] disabled:text-[#CBD5E1]"
              data-testid="cms-blog-prev"
            >← Previous</button>
            <span className="text-[#64748B]">Page {state.page}</span>
            <button
              disabled={!state.has_more}
              onClick={() => load(state.page + 1)}
              className="text-[#1A3E6F] disabled:text-[#CBD5E1]"
              data-testid="cms-blog-next"
            >Next →</button>
          </div>
        )}
      </div>
    </PublicShell>
  );
}

function Loading() {
  return <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center text-[#94A3B8]">Loading…</div>;
}
function ErrorBlock({ text }) {
  return <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center text-[#B91C1C]">{text}</div>;
}
